import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, Task, TimeEntry } from '../../../types';
import { getProjectTimeEntries, logTimeEntry, deleteTimeEntry } from '../../../services/timeService';
import { getProjectTasks } from '../../../services/taskService';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TEXTAREA,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

type DateRange = '7' | '30' | 'month' | 'all';
type UserFilter = 'all' | 'me';

export default function TimeTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_timetracking">
      <TimeTabBody {...props} />
    </ModuleGuard>
  );
}

function TimeTabBody({ pca, projectId, project }: Props) {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [tasks, setTasks]     = useState<Task[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<TimeEntry | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('30');
  const [userFilter, setUserFilter] = useState<UserFilter>('all');
  const me = useCurrentUser(pca, projectId);

  const reload = async () => {
    const [e, t] = await Promise.all([
      getProjectTimeEntries(pca, projectId),
      getProjectTasks(pca, projectId).catch(() => [] as Task[]),
    ]);
    setEntries(e); setTasks(t);
  };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const taskNameById: Record<string, string> = {};
  tasks.forEach(t => { taskNameById[t.dagrun_pm_taskid] = t.dagrun_pm_name; });

  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoff = (() => {
      if (dateRange === '7')  return now - 7 * 86400000;
      if (dateRange === '30') return now - 30 * 86400000;
      if (dateRange === 'month') {
        const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d.getTime();
      }
      return -Infinity;
    })();
    return entries.filter(e => {
      const ts = new Date(e.dagrun_pm_logdate).getTime();
      if (ts < cutoff) return false;
      if (userFilter === 'me' && me && e.dagrun_pm_userid !== me.oid) return false;
      return true;
    });
  }, [entries, dateRange, userFilter, me]);

  const totalHours    = filtered.reduce((s, e) => s + (e.dagrun_pm_hours ?? 0), 0);
  const billableHours = filtered.filter(e => e.dagrun_pm_isbillable).reduce((s, e) => s + (e.dagrun_pm_hours ?? 0), 0);
  const nonBillable   = totalHours - billableHours;
  const fmt = (n: number) => `${n.toFixed(1)} hrs`;

  const groups = useMemo(() => {
    const byDate = new Map<string, TimeEntry[]>();
    for (const e of filtered) {
      const key = (e.dagrun_pm_logdate ?? '').slice(0, 10);
      const arr = byDate.get(key) ?? [];
      arr.push(e);
      byDate.set(key, arr);
    }
    return Array.from(byDate.entries()).sort((a, b) => a[0] < b[0] ? 1 : -1);
  }, [filtered]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTimeEntry(pca, confirmDelete.dagrun_pm_timeentryid);
      setConfirmDelete(null);
      reload();
    } catch { /* noop */ }
  };

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Time Tracking</h3>
        <button type="button" onClick={() => setShowLog(true)} disabled={!me}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: me ? 1 : 0.5 }}
          onMouseEnter={e => me && (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Log Time</button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <SummaryCard label="Total Hours" value={fmt(totalHours)} />
        <SummaryCard label="Billable" value={fmt(billableHours)} accent="#0E7490" />
        <SummaryCard label="Non-Billable" value={fmt(nonBillable)} accent="#5A6272" />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        <label style={{ fontSize: 12, color: '#5A6272' }}>Date Range</label>
        <select value={dateRange} onChange={e => setDateRange(e.target.value as DateRange)} style={{ ...BM_INPUT, width: 'auto', padding: '6px 10px', fontSize: 13 }}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="month">This month</option>
          <option value="all">All time</option>
        </select>
        <label style={{ fontSize: 12, color: '#5A6272', marginLeft: 8 }}>User</label>
        <select value={userFilter} onChange={e => setUserFilter(e.target.value as UserFilter)} style={{ ...BM_INPUT, width: 'auto', padding: '6px 10px', fontSize: 13 }}>
          <option value="all">All</option>
          <option value="me">Just Me</option>
        </select>
      </div>

      {groups.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No time entries match these filters.</div>
      ) : (
        groups.map(([date, rows]) => {
          const subtotal = rows.reduce((s, e) => s + (e.dagrun_pm_hours ?? 0), 0);
          return (
            <div key={date} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid #DDE0E6' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {new Date(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span style={{ fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{subtotal.toFixed(1)} hrs</span>
              </div>
              {rows.map(e => {
                const taskName = e._dagrun_pm_taskid_value ? taskNameById[e._dagrun_pm_taskid_value] : null;
                const canDelete = me && e.dagrun_pm_userid === me.oid;
                return (
                  <div key={e.dagrun_pm_timeentryid} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '0.5px solid #EAECF0' }}>
                    <span style={{ flex: 1, fontSize: 14, color: '#0F1117' }}>{taskName ? truncate(taskName, 40) : <span style={{ color: '#B8BEC9' }}>—</span>}</span>
                    <span style={{ fontSize: 13, color: '#0F1117', fontFamily: 'var(--font-mono)', minWidth: 60, textAlign: 'right' }}>{e.dagrun_pm_hours.toFixed(1)} hrs</span>
                    {e.dagrun_pm_isbillable
                      ? <Badge bg="#D4EEF5" fg="#0A5568">Billable</Badge>
                      : <Badge bg="#F3F4F6" fg="#5A6272">Non-Billable</Badge>}
                    <span style={{ fontSize: 12, color: '#5A6272', minWidth: 110 }}>{e.dagrun_pm_username ?? (e.dagrun_pm_userid ?? '').slice(0, 8) ?? '—'}</span>
                    <span style={{ flex: 1, fontSize: 12, color: '#9198A8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.dagrun_pm_notes ? truncate(e.dagrun_pm_notes, 50) : ''}</span>
                    {canDelete && (
                      <button type="button" onClick={() => setConfirmDelete(e)} aria-label="Delete entry"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9198A8', fontSize: 16, padding: 2 }}
                        onMouseEnter={ev => (ev.currentTarget.style.color = '#DC2626')}
                        onMouseLeave={ev => (ev.currentTarget.style.color = '#9198A8')}
                      >×</button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      )}

      {showLog && me && (
        <LogTimeModal
          pca={pca}
          projectId={projectId}
          isBillableDefault={!!project.dagrun_pm_isbillable}
          tasks={tasks}
          meOid={me.oid}
          meName={me.displayName}
          taskName={null}
          taskId={null}
          allowTaskSelect
          onClose={() => setShowLog(false)}
          onCreated={() => { setShowLog(false); reload(); }}
        />
      )}

      {confirmDelete && (
        <BoardModal open title="Delete Time Entry" onClose={() => setConfirmDelete(null)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(null)} />
            <BMDangerButton onClick={doDelete}>Delete</BMDangerButton>
          </>}>
          Delete this time entry? This cannot be undone.
        </BoardModal>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 160, background: '#F5F6F8', borderRadius: 8, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent ?? '#0F1117', marginTop: 6, fontFamily: 'var(--font-display)' }}>{value}</div>
    </div>
  );
}

// ── Log Time Modal — exported so TaskDetailDrawer can reuse ─────────
export interface LogTimeModalProps {
  pca: IPublicClientApplication;
  projectId: string;
  isBillableDefault: boolean;
  tasks?: Task[];
  meOid: string;
  meName: string;
  taskId: string | null;
  taskName: string | null;
  allowTaskSelect?: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function LogTimeModal({
  pca, projectId, isBillableDefault, tasks = [], meOid, meName,
  taskId, taskName, allowTaskSelect, onClose, onCreated,
}: LogTimeModalProps) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(taskId);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState('1');
  const [billable, setBillable] = useState(isBillableDefault);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskQuery, setTaskQuery] = useState('');

  const hoursNum = parseFloat(hours);
  const valid = !isNaN(hoursNum) && hoursNum >= 0.1 && !!date;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true); setError(null);
    try {
      await logTimeEntry(pca, {
        projectId,
        taskId: selectedTaskId ?? null,
        userId: meOid,
        userName: meName,
        logDate: date,
        hours: hoursNum,
        isBillable: billable,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Log time failed');
    } finally { setSaving(false); }
  };

  const filteredTasks = taskQuery.trim()
    ? tasks.filter(t => t.dagrun_pm_name.toLowerCase().includes(taskQuery.toLowerCase())).slice(0, 8)
    : tasks.slice(0, 20);

  return (
    <BoardModal open title="Log Time" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !valid}>{saving ? 'Logging…' : 'Log Time'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Task</label>
        {allowTaskSelect ? (
          <>
            <input value={taskQuery} onChange={e => setTaskQuery(e.target.value)} placeholder="Search tasks…" style={{ ...BM_INPUT, marginBottom: 6 }} />
            <select value={selectedTaskId ?? ''} onChange={e => setSelectedTaskId(e.target.value || null)} style={BM_INPUT}>
              <option value="">— No task (project-level) —</option>
              {filteredTasks.map(t => <option key={t.dagrun_pm_taskid} value={t.dagrun_pm_taskid}>{truncate(t.dagrun_pm_name, 60)}</option>)}
            </select>
          </>
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#F5F6F8', borderRadius: 16, fontSize: 13, color: '#0F1117' }}>
            {taskName ?? '— Project-level —'}
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Date *</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Hours *</label>
          <input type="number" min={0.1} step={0.25} value={hours} onChange={e => setHours(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer', marginBottom: 14 }}>
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
        Billable
      </label>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} style={BM_TEXTAREA} placeholder="Optional" />
      </div>
      {error && <div style={{ color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
